import type { ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { articleById, type ArticleBlock } from '../../content/wechat';
import { getResearchProject } from '../../content/research-projects';

function linkify(text: string, keyPrefix: string): ReactNode[] {
  const pieces = text.split(/(https?:\/\/[^\s，。；）]+)/g);
  return pieces.map((piece, index) =>
    /^https?:\/\//.test(piece) ? (
      <a
        key={`${keyPrefix}-link-${index}`}
        href={piece}
        target="_blank"
        rel="noreferrer"
        className="article-inline-link"
      >
        {piece}
      </a>
    ) : (
      piece
    ),
  );
}

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`${keyPrefix}-b-${index}`}>{part.slice(2, -2)}</strong>;
    }
    return <span key={`${keyPrefix}-t-${index}`}>{linkify(part, `${keyPrefix}-${index}`)}</span>;
  });
}

function renderBlock(block: ArticleBlock, index: number) {
  const key = `block-${index}`;
  switch (block.type) {
    case 'h2':
      return <h2 key={key}>{renderInline(block.text ?? '', key)}</h2>;
    case 'h3':
      return <h3 key={key}>{renderInline(block.text ?? '', key)}</h3>;
    case 'quote':
      return <blockquote key={key}>{renderInline(block.text ?? '', key)}</blockquote>;
    case 'list':
      return (
        <ul key={key}>
          {(block.items ?? []).map((item) => (
            <li key={item}>{renderInline(item, key)}</li>
          ))}
        </ul>
      );
    case 'img':
      return (
        <figure key={key} className="article-figure">
          <img src={block.src} alt={block.caption ?? ''} loading="lazy" />
          {block.caption ? <figcaption>{block.caption}</figcaption> : null}
        </figure>
      );
    default:
      return <p key={key}>{renderInline(block.text ?? '', key)}</p>;
  }
}

export default function ResearchArticle() {
  const { projectId, articleId } = useParams<{ projectId: string; articleId: string }>();
  const project = getResearchProject(projectId);
  const article = articleId ? articleById.get(articleId) : undefined;

  if (!project || !article || !article.projects.includes(project.id)) {
    return <Navigate to="/" replace />;
  }

  return (
    <article className="wechat-article">
      <header className="article-head">
        <Link to={`/research/${project.id}`} className="article-back">
          ← {project.title}
        </Link>
        <h1>{article.title}</h1>
        <div className="article-head-meta">
          <span>{article.date}</span>
          <span className="badge">{article.primary === project.id ? '本项目' : '相关研究'}</span>
          <a href={article.source} target="_blank" rel="noreferrer" className="muted">
            公众号原文
          </a>
        </div>
      </header>

      <div className="article-body">{article.blocks.map(renderBlock)}</div>

      <footer className="article-foot">
        <a href={article.source} target="_blank" rel="noreferrer" className="btn btn-primary">
          在公众号查看原文
        </a>
        <Link to={`/research/${project.id}`} className="btn btn-ghost">
          返回项目
        </Link>
      </footer>
    </article>
  );
}